import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";

import type { ISelectedItem } from "@/data/model/Bill";
import type { IPaymentIntent, IPaymentMethod } from "@/data/model/Customer";
import type { IPaymentMethodsReportData } from "@/data/model/PaymentMethodsReportData";
import type { IService } from "@/data/model/Service";
import { config } from "@/data/util/Config";

import { translations } from "@/shared/localization/translations";

import { PayContext } from "@/operator/movedComponents/PayContext";

export const usePaymentStore = defineStore("payment", () => {
    const payContext = new PayContext(0);

    const lastPaymentMethodsData = ref<IPaymentMethodsReportData | null>(null);
    // state
    const paymentService = ref<IService | null>(null);
    const payLaterPaymentMethod = {
        id: config.payLaterPaymentMethod,
        type: config.payLaterPaymentMethod,
        provider: undefined,
        defaultProvider: null,
        name: config.payLaterPaymentMethod,
        localizedName: {
            de: "Später bezahlen",
            en: "Pay later",
            fr: "Payer plus tard",
            it: "Pagare più tardi",
        },
        imagePath: "/assets/img/brands/pay_later.svg",
        mode: 1,
        order: 9,
        defaultEnabled: true,
        isCardScheme: false,
        displayCollectiveCardIcon: true,
        isProviderEditable: false,
        useCollectiveCardIcon: false,
        isEditEnabled: false,
        isPaymentDevice: false,
        allowTipOnlyOnDevice: false,
        revenueEffective: true,
        isTipAllowed: true,
    } as IPaymentMethod;

    const discountsPaymentMethod = {
        id: config.discountsPaymentMethod,
        type: config.discountsPaymentMethod,
        provider: undefined,
        defaultProvider: null,
        name: config.discountsPaymentMethod,
        localizedName: {
            de: "Rabatt / Gutschein",
            en: "Discount / Voucher",
            fr: "Remise / Bon",
            it: "Sconto / Voucher",
        },
        imagePath: "/assets/img/brands/percent.svg",
        mode: 1,
        order: 10,
        defaultEnabled: true,
        isCardScheme: false,
        displayCollectiveCardIcon: true,
        isProviderEditable: false,
        useCollectiveCardIcon: false,
        isEditEnabled: false,
        isPaymentDevice: false,
        allowTipOnlyOnDevice: false,
        revenueEffective: false,
        isTipAllowed: true,
    } as IPaymentMethod;

    const vouchersPaymentMethod = {
        ...discountsPaymentMethod,
        imagePath: "/assets/img/brands/ticket.svg",
    } as IPaymentMethod;

    const isLoadingPaymentIntent = ref(false);
    const isProcessingPayment = ref(false);

    // actions
    async function setPaymentService(service: IService | null) {
        paymentService.value = service;
    }

    async function setLastPaymentMethodsData(service: IPaymentMethodsReportData | null) {
        lastPaymentMethodsData.value = service;
    }

    async function prepareForCheckout(billId: string, tip: number): Promise<IPaymentIntent> {
        try {
            isLoadingPaymentIntent.value = true;
            if (!paymentService.value) throw translations.pay.noActivePaymentService;

            await payContext.prepareForCheckout(paymentService.value, undefined, billId, tip);
            if (!payContext.intent) throw translations.cart.notAbleToPrepareForCheckout;

            return payContext.intent;
        } finally {
            isLoadingPaymentIntent.value = false;
        }
    }

    async function pay(
        consumption: number,
        totalDiscount: number,
        tip: number,
        selectedItems: ISelectedItem[],
        paymentMethod: IPaymentMethod
    ): Promise<void> {
        try {
            isProcessingPayment.value = true;
            if (!paymentService.value) throw translations.pay.noActivePaymentService;

            payContext.tipAmount = tip;
            payContext.selectedPaymentMethod = paymentMethod;

            await payContext.pay({
                selectedService: paymentService.value,
                cartHash: null,
                consumption,
                totalDiscount,
                tip,
                force: true,
                selectedItems,
            });
        } finally {
            isProcessingPayment.value = false;
        }
    }

    return {
        // state
        paymentService,
        payLaterPaymentMethod,
        discountsPaymentMethod,
        vouchersPaymentMethod,
        isLoadingPaymentIntent,
        isProcessingPayment,
        lastPaymentMethodsData,

        // getters

        // actions
        setLastPaymentMethodsData,
        setPaymentService,
        prepareForCheckout,
        pay,
    };
});

export type PaymentStore = ReturnType<typeof usePaymentStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(usePaymentStore, import.meta.hot));
}
